import {
  pathOr,
  uniq,
  set,
  lensPath,
  equals,
  filter,
  propEq,
  map,
  prop,
  path,
  find,
} from 'ramda';
import { compose } from 'recompose';
import { isNotEmpty } from 'src/utilities/hasValue';
import { TRUE } from 'src/constants';
import { META_DATA } from 'src/services/constants';
import { buildLabel } from 'src/utilities/naming';
import {
  getAnswerAndMkAttachment,
  getReducerFnForApiResource,
  getIdBasedOnResource,
  getUpdateFnForApiResource,
  updateApiResouceWithoutDataObject,
} from 'src/features/core/ChatEngine/Bot/simpleBotServiceUtil';
import { LENDSMART_SH_API_RESOURCE_WRITE } from 'src/features/core/Intents/intentConstants';
import { checkDeactivatedUponOnStatusContext } from '../../Engine/deactivatedUpon';

//////////////////  PUBLIC FUNCTIONS ////////////////////////

/**
 * getValueFromDataBag - gieves the value of given key from dataBag
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @param key contains a key as string
 * @returns value of given key from dataBag
 */
export const getValueFromDataBag = (dataBag: any, key: any) =>
  pathOr('', [key], dataBag);

/**
 * runAllAPIPromiseDispatchers method which runs list of promise request by
 * making as single requests if is there any duplicate requests and returns the responses
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @returns list of responses after running all promise dispatchers
 */
export const runAllAPIPromiseDispatchers = (dataBag: any) => {
  const { requests, apiData, dispatch } = dataBag;
  const unifiedRequest = uniq(requests);
  return Promise.all(
    unifiedRequest &&
    unifiedRequest.map((request: any) => {
      const structureData = updateApiResouceWithoutDataObject(request?.uR)
        ? apiData[request?.uR]
        : { data: apiData[request?.uR] };
      if (request?.callFn) {
        return dispatch(request?.callFn(structureData));
      }
      return;
    })
  );
};

const getApiResourceName = (dataBag:any) => {
  const { apiResource, singleSiteIntent } = dataBag
  const apiContextResourceName = pathOr("", ["api_context", 0, "context"], singleSiteIntent)
  return isNotEmpty(apiResource) ? apiResource : apiContextResourceName;

}

export const updateReducers = (dataBag: any) => {
  const {  dispatch } = dataBag;
  const apiResource =getApiResourceName(dataBag)
  const getFn = getReducerFnForApiResource(apiResource);
  const id = getIdBasedOnResource(apiResource);
  return Promise.all([dispatch(getFn(id))]);
};

/**
 * shouldAPIToBeUpdated method which checks and gives
 * whether the given intent needs to be updated with API
 * @param intent single intent
 * @returns boolean(true or false)
 */
export const shouldAPIToBeUpdated = (intent: any) =>
  equals(pathOr(TRUE, [META_DATA, buildLabel('is_api_update')], intent), TRUE);

/**
 * isAPIArrayType method which checks whether save in API is ARRAY type or not
 * @param saveInPath save_in_api_field array from intent config
 * @returns boolean(true or false)
 */
export const isAPIArrayType = (saveInPath: any) =>
  propEq('type', 'ARRAY')(saveInPath);

/**
 * isAPIPathType emethod which checks whether save in API is PATH type or not
 * @param saveInPath save_in_api_field array from intent config
 * @returns boolean(true or false)
 */
export const isAPIPathType = (saveInPath: any) =>
  !isNotEmpty(pathOr('', ['type'], saveInPath));

/**
 * getArrayTypeData - get the ARRAY type objects thats in save_in_api_field in intents config
 * @param saveInPaths save_in_api_field array from intent config
 * @returns the list of save_in_api_field of ARRAY type
 */
export const getArrayTypeData = (saveInPaths: any) =>
  filter(isAPIArrayType, saveInPaths);

/**
 * getMappingKeys method which returns the list of unified is_mapped_with_same_key
 * for ARRAY type save_in_api_field
 * @param arrayTypePaths ARRAY type list from save_in_api_field array in intents config
 * @returns list of unique is_mapped_with_same_key
 */
export const getMappingKeys = (arrayTypePaths: any) =>
  uniq(
    map(
      (saveInPath: any) => prop('is_mapped_with_same_key', saveInPath),
      arrayTypePaths
    )
  );

/**
 * dataFeederForCommonDataType method which returns object which has
 * different values used for updateAPIResourceFn
 * @param _apiResourceData contains API resources as objects
 * @param singleSiteIntent single intent
 * @returns object which has different values
 */
export const dataFeederForCommonDataType = (
  _apiResourceData: any,
  singleSiteIntent: any
) => {
  const requests: any = [];
  const saveInThisPaths = pathOr([], ['save_in_api_field'], singleSiteIntent);
  const apiResource = pathOr(
    '',
    ['metadata', LENDSMART_SH_API_RESOURCE_WRITE],
    singleSiteIntent
  );
  const apiResourceData = path([apiResource], _apiResourceData);
  const currenQuestionId = pathOr('', ['question_id'], singleSiteIntent);
  const filterArrayTypeData = getArrayTypeData(saveInThisPaths);

  const filterPathTypeData = getPathTypeData(saveInThisPaths);

  const mappingKeys = getMappingKeys(filterArrayTypeData);
  return {
    _apiResource: _apiResourceData,
    _apiResourceData,
    mappingKeys,
    filterPathTypeData,
    currenQuestionId,
    filterArrayTypeData,
    apiData: apiResourceData,
    apiResource,
    requests,
    saveInThisPaths,
    apiResourceData,
  };
};

/**
 * isMapWithSameKey method which checks is_mapped_with_same_key for given save_in_api_field
 * with given is_mapped_with_same_key as string
 * @param path single ARRAY type save_in_api_field object
 * @param mappingKey key of is_mapped_with_same_key of single intent config
 * @returns boolean(true or false)
 */
export const isMapWithSameKey = (path: any, mappingKey: any) =>
  equals(pathOr('', ['is_mapped_with_same_key'], path), mappingKey);

/**
 * checkAndGetAnswerFrmUserInput method which check the intent has answer,
 * if answer is same as key (which is used in ARRYA type) then key will returned
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @param path single object from save_api_field list
 * @returns answer as string
 */
export const checkAndGetAnswerFrmUserInput = (dataBag: any, path: any) => {
  const {
    singleSiteIntent,
    storeState,
    answer,
    _questionId: questionId,
    currenQuestionId,
  } = dataBag;

  const answerPreFilled = getAnswerAndMkAttachment(
    pathOr('', ['mapped_field_key'], path),
    singleSiteIntent,
    storeState,
    true
  );

  const _answer =
    !equals(questionId, currenQuestionId) || isNotEmpty(answerPreFilled)
      ? answerPreFilled
      : answer;

  return _answer;
};

/**
 * mkPromiseDispatchersForAPIUpdateable method which makes dispatchers
 * for the various intents that is to be updated
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @param apiDetails updated various API resource
 * (not from API,its internally updated by function,finally will be give to API)
 * @returns updated dataBag
 */
export const mkPromiseDispatchersForAPIUpdateable = (
  dataBag: any,
  apiDetails?: any
) => {

  const { apiResource, _apiResource, apiData } = dataBag;
  const readResource = pathOr(apiResource, ['readResource'], apiDetails);
  dataBag.apiData = compose(set(lensPath([readResource]), apiData))(
    _apiResource
  );

  return mkAPIDispatchers(readResource, dataBag);
};

//////////////////  PRIVATE FUNCTIONS ////////////////////////

/**
 * getPathTypeData get the PATH type objects thats in save_in_api_field in intents config
 * @param saveInPaths save_in_api_field array from intent config
 * @returns the list of save_in_api_field of PATH type
 */
const getPathTypeData = (saveInPaths: any) =>
  filter(isAPIPathType, saveInPaths);

/**
 * mkAPIDispatchers method which push the requests array with dispatchFn for the given readResource and readResource full object,
 * for eg:[{callFn:updateBusiness,readResource:<updated_business_object>}]
 * @param readResource which API resource as string , for eg:business,declarations
 * @param dataBag consists of required datas inside it which intiated in SimpleBotService File
 * @returns updated dataBag
 */
const mkAPIDispatchers = (readResource: string, dataBag: any) => {
  const { requests } = dataBag;
  const updateAction = getUpdateFnForApiResource(readResource);

  requests.push({ uR: readResource, callFn: updateAction });

  return set(lensPath(['requests']), requests, dataBag);
};

export const getActiveContext = (statusContext: any, intents: any) => {
  const currentStatusContext = find((context: any) => {
    const { shouldRun = false }: any = checkDeactivatedUponOnStatusContext(context, intents);
    return shouldRun;
  }, statusContext || []);
  return currentStatusContext;
}